import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import OperatorDashboard from "./pages/OperatorDashboard";
import IntakeForm from "./pages/IntakeForm";
import AllEntries from "./pages/AllEntries";
import Companies from "./pages/Companies";
import Admin from "./pages/Admin";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Pricing from "./pages/Pricing";
import LandingPage from "./pages/LandingPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AboutUs from "./pages/AboutUs";
import Settings from "./pages/Settings";
import EmailEngagement from "./pages/EmailEngagement";
import EmailInbox from "./pages/EmailInbox";
import LossReports from "./pages/LossReports";
import InsurancePolicies from "./pages/InsurancePolicies";
import DevHubLayout from "./pages/devhub/DevHubLayout";
import DevDashboard from "./pages/devhub/DevDashboard";
import DevTenants from "./pages/devhub/DevTenants";
import DevUsers from "./pages/devhub/DevUsers";
import DevDatabase from "./pages/devhub/DevDatabase";
import DevOrphans from "./pages/devhub/DevOrphans";
import DevUserLogins from "./pages/devhub/DevUserLogins";
import DevSystemHealth from "./pages/devhub/DevSystemHealth";

import BottomNav from "./components/BottomNav";
import DemoWalkthrough from "./components/DemoWalkthrough";
import ProviderOnboarding from "./components/ProviderOnboarding";
import ProviderWalkthrough from "./components/ProviderWalkthrough";
import RecycleBin from "./pages/RecycleBin";
import SupportQueue from "./pages/SupportQueue";
import Organizations from "./pages/Organizations";

import UserManagement from "./pages/UserManagement";
import AuditLog from "./pages/AuditLog";
import DevAuditLog from "./pages/devhub/DevAuditLog";
import DevDemoAnalytics from "./pages/devhub/DevDemoAnalytics";
import DevBilling from "./pages/devhub/DevBilling";
import DevOperator from "./pages/devhub/DevOperator";
import DevQuickAccess from "./pages/devhub/DevQuickAccess";
import DevPublicPages from "./pages/devhub/DevPublicPages";
import DevFeedback from "./pages/devhub/DevFeedback";
import DevEmailTemplates from "./pages/devhub/DevEmailTemplates";

import WelcomePage from "./pages/WelcomePage";
import JoinPage from "./pages/JoinPage";
import CustomerPortal from "./pages/CustomerPortal";
import { DemoBanner } from "./components/DemoBanner";
import { useDemoPageTracker } from "./components/DemoTracker";
import { NotificationProvider } from "./contexts/NotificationContext";

// Send cookies with every request
axios.defaults.withCredentials = true;

// Quick-Access: capture token from URL before anything renders (tab-scoped via sessionStorage)
(function handleQaToken() {
  const params = new URLSearchParams(window.location.search);
  const qaToken = params.get('qa_token');
  if (qaToken) {
    sessionStorage.setItem('qa_session_token', qaToken);
    params.delete('qa_token');
    const clean = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));
  }
})();

// Axios interceptor: if this tab has a quick-access token, send it as Bearer (overrides cookie)
axios.interceptors.request.use((config) => {
  const qaToken = sessionStorage.getItem('qa_session_token');
  if (qaToken) {
    config.headers = { ...config.headers, Authorization: `Bearer ${qaToken}` };
  }
  return config;
});

function DevProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" /></div>;
  if (!user) return <Login />;
  if (!user.is_developer) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><p className="text-slate-400">Developer access required</p></div>;
  return children;
}

function HomeRoute({ children }) {
  const { user, loading, operatorContext } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }
  if (!user) return <LandingPage />;
  // Operator with context active → show regular Home for the selected tenant
  if (user.is_platform_operator && operatorContext?.tenantId) {
    return children;
  }
  // Operator with no context → show OperatorDashboard
  if (user.is_platform_operator) {
    return <ProtectedRoute><Navbar /><OperatorDashboard /><BottomNav /></ProtectedRoute>;
  }
  return children;
}

function OperatorGuard({ children }) {
  const { user, operatorContext } = useAuth();
  if (!user) return children;
  // Operator with context active → allow access to tenant routes
  if (user.is_platform_operator && operatorContext?.tenantId) return children;
  // Operator without context → redirect to home
  if (user.is_platform_operator) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useDemoPageTracker();

  // Check URL fragment for session_id (Google OAuth callback) - synchronous check prevents race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/join/:code" element={<JoinPage />} />
      <Route path="/portal/claim/:token" element={<CustomerPortal />} />

      {/* Dev Hub - completely separate section */}
      <Route path="/dev-hub" element={<DevProtected><DevHubLayout /></DevProtected>}>
        <Route index element={<DevDashboard />} />
        <Route path="operator" element={<DevOperator />} />
        <Route path="quick-access" element={<DevQuickAccess />} />
        <Route path="tenants" element={<DevTenants />} />
        <Route path="users" element={<DevUsers />} />
        <Route path="database" element={<DevDatabase />} />
        <Route path="orphans" element={<DevOrphans />} />
        <Route path="user-logins" element={<DevUserLogins />} />
        <Route path="system-health" element={<DevSystemHealth />} />
        <Route path="public-pages" element={<DevPublicPages />} />
        <Route path="feedback" element={<DevFeedback />} />
        <Route path="email-templates" element={<DevEmailTemplates />} />
        <Route path="audit-log" element={<DevAuditLog />} />
        <Route path="billing" element={<DevBilling />} />
        <Route path="demo-analytics" element={<DevDemoAnalytics />} />
      </Route>

      {/* Protected routes */}
      <Route path="/" element={
        <HomeRoute>
          <ProtectedRoute><Navbar /><Home /><BottomNav /></ProtectedRoute>
        </HomeRoute>
      } />
      <Route path="/intake" element={<OperatorGuard><ProtectedRoute roles={["super_admin","admin","manager","member"]}><Navbar /><IntakeForm /><BottomNav /></ProtectedRoute></OperatorGuard>} />
      <Route path="/all-entries" element={<OperatorGuard><ProtectedRoute><Navbar /><AllEntries /><BottomNav /></ProtectedRoute></OperatorGuard>} />
      <Route path="/companies" element={<OperatorGuard><ProtectedRoute><Navbar /><Companies /><BottomNav /></ProtectedRoute></OperatorGuard>} />
      <Route path="/admin" element={<OperatorGuard><ProtectedRoute roles={["super_admin","admin","manager"]}><Navbar /><Admin /><BottomNav /></ProtectedRoute></OperatorGuard>} />
      <Route path="/organizations" element={<OperatorGuard><ProtectedRoute roles={["super_admin","admin"]}><Navbar /><Organizations /><BottomNav /></ProtectedRoute></OperatorGuard>} />
      <Route path="/recycle-bin" element={<OperatorGuard><ProtectedRoute roles={["super_admin","admin"]}><Navbar /><RecycleBin /><BottomNav /></ProtectedRoute></OperatorGuard>} />
      <Route path="/user-management" element={<ProtectedRoute roles={["super_admin","admin","manager"]}><Navbar /><UserManagement /><BottomNav /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute roles={["super_admin","admin"]}><Navbar /><AuditLog /><BottomNav /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute roles={["super_admin","admin"]}><Navbar /><SupportQueue /><BottomNav /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute roles={["super_admin","admin"]}><Navbar /><Settings /><BottomNav /></ProtectedRoute>} />
      <Route path="/email/engagement" element={<ProtectedRoute><Navbar /><EmailEngagement /><BottomNav /></ProtectedRoute>} />
      <Route path="/inbox" element={<OperatorGuard><ProtectedRoute><Navbar /><EmailInbox /><BottomNav /></ProtectedRoute></OperatorGuard>} />
      <Route path="/reports" element={<OperatorGuard><ProtectedRoute roles={["super_admin","admin","manager"]}><Navbar /><LossReports /><BottomNav /></ProtectedRoute></OperatorGuard>} />
      <Route path="/insurance/policies" element={<OperatorGuard><ProtectedRoute roles={["super_admin","admin","manager"]}><Navbar /><InsurancePolicies /><BottomNav /></ProtectedRoute></OperatorGuard>} />
    </Routes>
  );
}

function DemoWalkthroughWrapper() {
  const { user } = useAuth();
  const demoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  // Don't show demo tour for provider accounts, platform operators, or users still onboarding
  if (!user || !demoMode || user.account_type === 'provider' || user.is_platform_operator || !user.onboarding_completed) return null;
  return <DemoWalkthrough />;
}

function ProviderOnboardingWrapper() {
  const { user } = useAuth();
  if (!user || user.account_type !== 'provider' || user.is_platform_operator) return null;
  return <ProviderOnboarding />;
}

function ProviderWalkthroughWrapper() {
  const { user } = useAuth();
  const demoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  if (!user || !demoMode || user.account_type !== 'provider' || user.is_platform_operator) return null;
  return <ProviderWalkthrough />;
}

import FeedbackButton from "./components/FeedbackButton";

function FeedbackWrapper() {
  const { user } = useAuth();
  const location = useLocation();
  const hidePaths = ['/landing', '/login', '/register', '/pricing', '/about', '/join'];
  if (!user || location.pathname.startsWith('/dev-hub') || hidePaths.some(p => location.pathname.startsWith(p))) return null;
  return <FeedbackButton />;
}

function NotifWrapper({ children }) {
  const { user } = useAuth();
  if (!user) return children;
  return <NotificationProvider>{children}</NotificationProvider>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotifWrapper>
          <DemoBanner />
          <AppRouter />
          <FeedbackWrapper />
          <DemoWalkthroughWrapper />
        </NotifWrapper>
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
